import { useEffect, useRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { NotificationDropdown } from "./NotificationDropdown";
import { useNotificationStore } from "../../stores/notification-store";
import { useUnreadCount } from "../../hooks/useNotifications";
import BellLinear from "~icons/solar/bell-linear";
import BellBold from "~icons/solar/bell-bold";
import { cn } from "../../lib/utils";

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

  // Fetch the baseline unread count on mount (polls every 60s as SSE fallback)
  const { data: unreadData } = useUnreadCount();
  useEffect(() => {
    if (unreadData?.unread_count != null) {
      setUnreadCount(unreadData.unread_count);
    }
  }, [unreadData, setUnreadCount]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className={cn(
          "relative flex size-9 cursor-pointer items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
          isOpen
            ? "border-primary/30 bg-primary/5 text-primary ring-2 ring-primary/10"
            : "border-black/[0.06] bg-white text-ink-soft shadow-[0px_1px_0px_rgba(0,0,0,0.05)] hover:bg-black/5 hover:text-ink"
        )}
        aria-label={
          unreadCount > 0
            ? `Notifications — ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Icon icon={isOpen ? BellBold : BellLinear} size="sm" decorative />

        {unreadCount > 0 && (
          <span
            className="absolute right-1.5 top-1.5 size-2 rounded-full bg-primary ring-2 ring-white"
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && <NotificationDropdown onClose={() => setIsOpen(false)} />}
    </div>
  );
}
